export interface MySwitchOptions<T> {
  equalityCheck: (v1: T, v2: T) => boolean;
}

class MySwitch<TValue, TRet> {
  private readonly value: TValue;
  private readonly equalityCheck: MySwitchOptions<TValue>["equalityCheck"];
  private returnedValue: TRet | undefined = undefined;

  constructor(value: TValue, options: Partial<MySwitchOptions<TValue>> = {}) {
    const { equalityCheck } = options;
    this.value = value;
    this.equalityCheck = equalityCheck ?? ((v1, v2) => v1 === v2);
  }

  case<V extends TValue>(
    conditions: V[],
    then: TRet,
  ): MySwitch<Exclude<TValue, V>, TRet>;
  case<V extends TValue>(
    condition: V,
    then: TRet,
  ): MySwitch<Exclude<TValue, V>, TRet>;
  case<V extends TValue>(condition: V | V[], then: TRet) {
    if (this.returnedValue === undefined) {
      const conditions = Array.isArray(condition) ? condition : [condition];
      for (const cond of conditions) {
        if (this.equalityCheck(this.value, cond)) {
          this.returnedValue = then;
          break;
        }
      }
    }

    return this as MySwitch<Exclude<TValue, V>, TRet>;
  }

  done() {
    return this.returnedValue as [TValue] extends [never]
      ? TRet
      : TRet | undefined;
  }

  default(value: TRet) {
    this.returnedValue ??= value;
    return this.returnedValue;
  }
}

export type ClassConstructorArgs<T> = T extends new (...args: infer A) => any
  ? A
  : never;

export function switchToValue<TValue, TRet>(
  ...args: ClassConstructorArgs<typeof MySwitch<TValue, TRet>>
) {
  return new MySwitch<TValue, TRet>(...args);
}
